import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import _ from 'lodash';
import fp from 'lodash/fp';
import moment from 'moment-timezone';
import { position } from 'polished';
import {
  color,
  media,
  toCurrency,
  getDiscountValue as getDiscountValueHelper,
} from '@deerplusofficial/pos-helper';
import {
  ButtonBase,
  Icon,
  Spinner,
  Button,
} from '@deerplusofficial/pos-components';
import { withPrintInvoice, withCancelOrder } from '../../model/Mutations';
import { LIMIT } from '../../model/Queries/withQueryOrders';
import { Pagination } from '../../components';

const timezone = moment.tz.guess();

const HideOnMobile = styled.div`
  display: none;
  ${media(
    'tablet',
    css`
      display: block;
    `
  )};
`;

const Conainer = styled.div`
  position: relative;
  ${props =>
    props.loading &&
    css`
      &::before {
        content: '';
        background-color: rgba(0, 0, 0, 0.2);
        ${position('absolute', '0', '0', '0', '0')};
      }
    `};
`;

const Table = styled.table`
  display: block;
  padding: 10px 10px 0;
  width: 100%;
  color: ${color.text.title};
  ${media(
    'tablet',
    css`
      display: table;
      padding: 20px;
    `
  )};
`;

const Th = styled.th`
  display: none;
  text-align: center;
  ${media(
    'tablet',
    css`
      display: table-cell;
      padding: 20px;
      border-bottom: solid 1px ${color.border};
    `
  )};
`;

const Tbody = styled.tbody`
  display: flex;
  flex-direction: column;
  ${media(
    'tablet',
    css`
      display: table-row-group;
    `
  )};
`;

const DataRow = styled.tr`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 12px;
  position: relative;
  border-bottom: solid 1px ${color.border};
  &:last-child {
    border-bottom: 0;
  }
  ${media(
    'tablet',
    css`
      display: table-row;
      padding: 0;
    `
  )};
`;

const Td = styled.td`
  display: block;
  text-align: ${({ align }) => align || 'right'};
  font-size: 12px;
  ${props =>
    props.hideOnMobile &&
    css`
      display: none;
    `};
  ${props =>
    props.nowrap &&
    css`
      white-space: nowrap;
    `};
  ${media(
    'tablet',
    css`
      display: table-cell;
      padding: ${props => (props.dense ? '10px' : '20px')};
      font-size: ${props => (props.indented ? '12px' : '14px')};
      color: ${props =>
        props.secondary ? color.text.secondary : color.text.title};
      border-bottom: ${props =>
        props.noBorder ? 0 : `solid 1px ${color.border}`};
      ${props =>
        props.indented &&
        css`
          text-indent: 20px;
        `};
    `
  )};
`;

const Method = Td.extend`
  ${position('absolute', '12px', '12px', null, null)};
  ${media(
    'tablet',
    css`
      position: static;
      text-align: center;
    `
  )};
`;

const OrderNumber = Td.extend`
  flex-grow: 1;
  margin-right: 68px;
  word-break: break-all;
  font-size: 16px;
  color: ${color.text.primary};
`;

const Name = Td.extend`
  width: 100%;
  font-size: 12px;
  text-align: left;
  color: ${color.text.secondary};
  ${media(
    'tablet',
    css`
      width: auto;
      text-align: center;
    `
  )};
`;

const AccordionRow = styled.tr`
  display: none;
  ${media(
    'tablet',
    css`
      display: table-row;
    `
  )};
  ${Td} {
    padding: 4px 20px;
  }
`;

const Date = Name.extend`
  display: flex;
  margin-top: 20px;
  ${media(
    'tablet',
    css`
      text-align: right;
    `
  )};
`;

const Label = styled.span`
  display: inline-block;
  color: white;
  line-height: 1;
  padding: 8px;
  white-space: nowrap;
  text-align: center;
  border-radius: 3px;
  background-color: ${props => props.color};
`;

const Action = styled.a.attrs({
  role: 'button',
  tabIndex: 0,
})`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  padding-left: 40px;
  ${media(
    'tablet',
    css`
      display: none;
    `
  )};
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 10px 20px;
  ${media(
    'tablet',
    css`
      justify-content: space-between;
      padding: 0 20px 20px;
      font-size: 12px;
      color: ${color.text.title};
    `
  )};
`;

const NotFound = styled.p`
  padding: 40px;
  text-align: center;
  font-size: 18px;
  color: ${color.text.title};
`;

const Loader = styled(Spinner).attrs({ size: 60 })`
  ${position('absolute', '50%', null, null, '50%')};
  transform: translate(-50%, -50%);
`;

const ButtonWrapper = styled.div`
  ${media(
    'tablet',
    css`
      display: grid;
      width: 100%;
      grid-column-gap: 12px;
      grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    `
  )};
`;

function getPaymentType({ type, name }) {
  const preDefinedLabels = {
    1: {
      name: '現金',
      color: color.primary,
    },
    2: {
      name: '信用卡',
      color: color.warning,
    },
    102: {
      name: 'LinePay',
      color: color.brand.line,
    },
  };

  return _.get(preDefinedLabels, type, { name, color: color.text.secondary });
}

function getDiscountValue({ unit_price, discounts, quantity }) {
  const { criteria, value, type } = _.isArray(discounts)
    ? discounts[0]
    : discounts;

  return getDiscountValueHelper({
    quantity,
    price: unit_price,
    isDiscountPlan: type === 1,
    formattedValue: criteria === 0 ? String(value) : `${value}%`,
  });
}

export function calculateSubtotal(orderDetails) {
  let subtotal = 0;

  _.forEach(orderDetails, orderDetail => {
    const { subtotal_price, discounts } = orderDetail;

    if (!discounts) return (subtotal += subtotal_price);

    subtotal += subtotal_price - getDiscountValue(orderDetail);
  });

  return subtotal;
}

function renderDiscount({ name, value, type }) {
  const isItemDiscount = type === 'item';
  const defaultName = isItemDiscount ? '商品手動折扣' : '訂單手動折扣';

  return (
    <AccordionRow>
      <Td noBorder dense />
      <Td
        noBorder
        secondary
        dense
        colSpan="2"
        align="left"
        indented={isItemDiscount}
      >
        {name || defaultName}
      </Td>
      <Td noBorder secondary dense colSpan="2" indented={isItemDiscount}>
        {toCurrency(-value)}
      </Td>
      <Td noBorder dense colSpan="3" />
    </AccordionRow>
  );
}

function renderOrderDiscount(order) {
  const { discounts, order_details: orderDetails } = order;

  if (!discounts) return null;

  const { name, criteria, value } = _.isArray(discounts)
    ? discounts[0]
    : discounts;

  const discountValue =
    criteria === 0
      ? value
      : _.round((calculateSubtotal(orderDetails) * value) / 100);

  return renderDiscount({ name, type: 'order', value: discountValue });
}

function renderItemDiscount(orderDetail) {
  const { name, discounts } = orderDetail;

  if (!discounts) return null;

  return renderDiscount({
    name,
    type: 'item',
    value: getDiscountValue(orderDetail),
  });
}

function renderItem(orderDetail) {
  const {
    product_item_name,
    product_item_id,
    quantity,
    subtotal_price,
  } = orderDetail;
  return (
    <React.Fragment key={product_item_id}>
      <AccordionRow>
        <Td noBorder />
        <Td noBorder align="left" colSpan="2">
          {product_item_name}
        </Td>
        <Td noBorder>{quantity}</Td>
        <Td noBorder secondary>
          {toCurrency(subtotal_price)}
        </Td>
        <Td noBorder colSpan="3" />
      </AccordionRow>
      {renderItemDiscount(orderDetail)}
    </React.Fragment>
  );
}

class OrdersList extends Component {
  static propTypes = {
    orders: PropTypes.arrayOf(PropTypes.shape()),
    orderId: PropTypes.string,
    totalCounts: PropTypes.number,
    page: PropTypes.number,
    loading: PropTypes.bool,
    onPageChange: PropTypes.func,
    onRefetch: PropTypes.func,
    onOrderDetailOpen: PropTypes.func,
    onOrderDetailClose: PropTypes.func,
  };

  static defaultProps = {
    page: 1,
    orderId: '',
    loading: false,
    onPageChange: _.identity,
    onRefetch: _.identity,
    onOrderDetailOpen: _.identity,
    onOrderDetailClose: _.identity,
  };

  get currentCounts() {
    const { page, totalCounts } = this.props;
    const start = (page - 1) * LIMIT + 1;
    const end = start + LIMIT - 1;

    return (
      <strong>
        {start} to {end > totalCounts ? totalCounts : end}
      </strong>
    );
  }

  get totalPages() {
    const { totalCounts } = this.props;

    if (!totalCounts) return 1;

    return _.ceil(_.divide(totalCounts, LIMIT));
  }

  onAccordionToggle = orderId => {
    const {
      onOrderDetailOpen,
      onOrderDetailClose,
      orderId: detailOrderId,
    } = this.props;

    detailOrderId === orderId
      ? onOrderDetailClose()
      : onOrderDetailOpen(orderId);
  };

  renderInvoiceButtons = ({ invoiceId, orderId }) => {
    const { onInvoicePrint, onInvoiceRePrint, onRefetch } = this.props;

    if (_.size(invoiceId))
      return (
        <Button
          accent
          onClick={() => onInvoiceRePrint({ invoiceId, refetch: onRefetch })}
        >
          補印發票
        </Button>
      );

    return (
      <Button
        accent
        onClick={() => onInvoicePrint({ orderId, refetch: onRefetch })}
      >
        開立電子發票
      </Button>
    );
  };

  renderAccordion = order => {
    const { onOrderCancelPrompt, onRefetch } = this.props;
    const {
      order_details,
      status,
      redeem_points,
      reward_points,
      invoice = {},
      id: orderId,
      order_no: orderNumber,
      total_price: totalPrice,
    } = order;
    const { vat_id, invoice_no, id: invoiceId } = invoice;
    const isCanceled = status === 200;

    return (
      <React.Fragment>
        <tr>
          <Td hideOnMobile noBorder dense colSpan="8" />
        </tr>

        {_.map(order_details, renderItem)}

        {renderOrderDiscount(order)}

        <AccordionRow>
          <Td noBorder />
          <Td align="left" colSpan={isCanceled ? 5 : 3}>
            {Boolean(reward_points) && <div>點數累積：{reward_points}</div>}
            {Boolean(redeem_points) && <div>點數折抵：{redeem_points}</div>}
            {invoice_no && <div>發票號碼：{invoice_no}</div>}
            {vat_id && <div>買受人統一編號：{vat_id}</div>}
          </Td>
          <Td noBorder colSpan={isCanceled ? 2 : 4}>
            <ButtonWrapper>
              {isCanceled ? (
                <Button disabled cancel>
                  訂單已取消
                </Button>
              ) : (
                <React.Fragment>
                  {this.renderInvoiceButtons({ invoiceId, orderId })}
                  <Button
                    secondary
                    onClick={() =>
                      onOrderCancelPrompt({
                        orderId,
                        orderNumber,
                        totalPrice,
                        refetch: onRefetch,
                      })
                    }
                  >
                    取消訂單
                  </Button>
                </React.Fragment>
              )}
            </ButtonWrapper>
          </Td>
        </AccordionRow>
        <tr>
          <Td hideOnMobile dense colSpan="8" />
        </tr>
      </React.Fragment>
    );
  };

  renderDataRow = order => {
    const { orderId } = this.props;
    const {
      id,
      order_no,
      creator,
      subtotal_price,
      discount,
      total_price,
      created_at,
      payment_method,
    } = order;
    const payment = getPaymentType(payment_method);

    return (
      <React.Fragment key={id}>
        <DataRow>
          <Td hideOnMobile align="center">
            <ButtonBase onClick={() => this.onAccordionToggle(id)}>
              <Icon icon={`chevron-${orderId === id ? 'up' : 'down'}`} />
            </ButtonBase>
          </Td>
          <OrderNumber align="left">{order_no}</OrderNumber>
          <Name>{creator}</Name>
          <Method>
            <Label color={payment.color}>{payment.name}</Label>
          </Method>
          <Td hideOnMobile nowrap>
            {toCurrency(subtotal_price)}
          </Td>
          <Td hideOnMobile nowrap>
            {toCurrency(-discount)}
          </Td>
          <Td hideOnMobile nowrap>
            {toCurrency(total_price)}
          </Td>
          <Date>
            {moment
              .utc(created_at)
              .tz(timezone)
              .format('YYYY/MM/DD hh:mm:ssA')}
            <Action onClick={() => this.onAccordionToggle(id)}>
              <Icon icon="chevron-right" />
            </Action>
          </Date>
        </DataRow>
        {orderId === id && this.renderAccordion(order)}
      </React.Fragment>
    );
  };

  render() {
    const { orders, totalCounts, page, onPageChange, loading } = this.props;

    return (
      <Conainer loading={loading}>
        {totalCounts === 0 ? (
          <NotFound>
            {loading ? '' : '對不起！你所指定的條件，暫無搜尋結果。'}
          </NotFound>
        ) : (
          <React.Fragment>
            <Table>
              <thead>
                <tr>
                  <Th />
                  <Th>訂單編號</Th>
                  <Th>銷售人員</Th>
                  <Th>支付方式</Th>
                  <Th>訂單總金額</Th>
                  <Th>折扣金額</Th>
                  <Th>結帳金額</Th>
                  <Th>銷售時間</Th>
                </tr>
              </thead>
              <Tbody>{_.map(orders, this.renderDataRow)}</Tbody>
            </Table>
            {this.totalPages > 1 && (
              <Footer>
                <HideOnMobile>
                  Showing {this.currentCounts} of {totalCounts} Order records
                </HideOnMobile>
                <Pagination
                  currentPage={page}
                  totalPages={this.totalPages}
                  onPageChange={onPageChange}
                />
              </Footer>
            )}
          </React.Fragment>
        )}
        {loading && <Loader />}
      </Conainer>
    );
  }
}

export default fp.compose(
  withCancelOrder(),
  withPrintInvoice()
)(OrdersList);



// WEBPACK FOOTER //
// ./src/pages/Orders/List.js